import React from "react";
import { Box, Chip, Grid, IconButton, Typography } from "@mui/material";
import TrashIcon from "@mui/icons-material/DeleteOutline";
import { CartItem, GACartItem } from "../../models/Cart";
import { priceFormatter } from "../../utils/helpers";
import CartStore from "../../stores/CartStore";

type Props = {
    item: CartItem;
    onRemove: () => void;
    cartStore: CartStore | null;
    onRemoveRedemptionCode: () => void;
};

const ASCartRow = ({
    item,
    onRemove,
    cartStore,
    onRemoveRedemptionCode,
}: Props) => {
    const getPriceNode = (): React.ReactNode => {
        if (item.comp) {
            return <Chip color="warning" label="COMP" size="small" />;
        }
        if (item.overridePrice) {
            return (
                <Typography variant="body2">
                    {priceFormatter(item.basePrice ?? item.overridePrice)}
                </Typography>
            );
        }
        if ((item.basePrice ?? item.department.price) === 0) {
            return <Chip color="success" label="FREE" size="small" />;
        }
        if (item.redemption) {
            return (
                <Chip
                    color="success"
                    size="small"
                    label={"Code: " + item.redemption.code}
                    onDelete={onRemoveRedemptionCode}
                />
            );
        }
        return (
            <Typography variant="body2">
                {priceFormatter(item.basePrice ?? item.department.price)}
            </Typography>
        );
    };

    const getDiscountNode = (): React.ReactNode => {
        if (item.comp) return null;
        if (item.overridePrice !== null) {
            return (
                <Typography variant="body2" color="success.main">
                    {priceFormatter(item.department.price - item.overridePrice)}{" "}
                    in discounts
                </Typography>
            );
        }
        if (item.discount) {
            return (
                <Typography variant="body2" color="success.main">
                    {priceFormatter(
                        item.discount.grossAmount ?? item.discount.amount,
                    )}{" "}
                    in discounts
                </Typography>
            );
        }
        return null;
    };

    const getServiceFeeNode = (): React.ReactNode => {
        if (item.comp) return null;
        return item.serviceFees.map((it) => (
            <Typography variant="body2" color="text.secondary" key={it.id}>
                + {priceFormatter(it.amount)} {it.name}
            </Typography>
        ));
    };


    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" fontWeight={600}>
                {item.seatDetails}
            </Typography>
            <Grid container spacing={1}>
                <Grid item xs={7}>
                    {getPriceNode()}
                    {getServiceFeeNode()}
                    {getDiscountNode()}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {item.acceptsModifications && (
                        <IconButton color="error" onClick={onRemove}>
                            <TrashIcon />
                        </IconButton>
                    )}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography variant="body2">
                        {priceFormatter(cartStore?.itemTotal(item) ?? 0)}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ASCartRow;
